import { queryNoTracking, update } from '../../services/DataService';
import { foldCasingMissesIntoResult } from '../workflow/workflowApi';

// Data queries (DataController partial: CustomizationsController.cs)
const q_Customizations_Grid = queryNoTracking('Customizations_Grid', 0);
const q_Customization_VersionsGrid = queryNoTracking('Customization_VersionsGrid', 0);
const q_Customization_GetDraft = queryNoTracking('Customization_GetDraft', 0);
const q_Customization_GetVersion = queryNoTracking('Customization_GetVersion', 0);
const q_Customization_PublishedStringOverrides = queryNoTracking('Customization_PublishedStringOverrides', 0);
const q_Customization_StringOverrideDescriptors = queryNoTracking('Customization_StringOverrideDescriptors', 0);
const q_Customization_GetAccess = queryNoTracking('Customization_GetAccess', 0);
const q_Customizations_PublishedForMe = queryNoTracking('Customizations_PublishedForMe', 0);
const q_Customization_PublishedDefinition = queryNoTracking('Customization_PublishedDefinition', 0);

export async function listCustomizations() {
  return await q_Customizations_Grid.getAll();
}

export async function getCustomizationVersions(customizationID) {
  return await q_Customization_VersionsGrid.getAll({ id: customizationID });
}

export async function getCustomizationDraft(customizationID) {
  return await q_Customization_GetDraft.getFirstOrDefault({ id: customizationID });
}

export async function getCustomizationVersion(customizationID, versionID) {
  return await q_Customization_GetVersion.getFirstOrDefault({ id: customizationID, versionID });
}

/**
 * The tenant's published string overrides, already resolved by the server through
 * its descriptor catalog to a { <strings-file path>: <replacement text> } map, or
 * null. Server returns null when the RTL_Customization module is not licensed, so
 * overrides can never apply on unlicensed tenants regardless of what is stored.
 */
export async function getPublishedStringOverrides() {
  const result = await q_Customization_PublishedStringOverrides.getFirstOrDefault({});
  const overrides = result?.Overrides ?? result?.overrides;
  return overrides && typeof overrides === 'object' ? overrides : null;
}

/**
 * The catalog of overridable strings ({ Id, Label, DefaultText }) for the management
 * editor. The C# StringOverrideDescriptors catalog is the single source of truth.
 */
export async function getStringOverrideDescriptors() {
  const rows = await q_Customization_StringOverrideDescriptors.getAll();
  return Array.isArray(rows) ? rows : [];
}

/** The customization's access grants: { users: [id], teams: [id], accessRoles: [id] }. */
export async function getCustomizationAccess(customizationID) {
  const result = await q_Customization_GetAccess.getFirstOrDefault({ id: customizationID });
  return {
    users: Array.isArray(result?.Users) ? result.Users : [],
    teams: Array.isArray(result?.Teams) ? result.Teams : [],
    accessRoles: Array.isArray(result?.AccessRoles) ? result.AccessRoles : [],
  };
}

/**
 * The published customizations the current user may consume
 * ([{ ID, Name, Description, CustomizationType }]). Module-gated server-side;
 * non-managers only see customizations whose access grants name them.
 */
export async function listPublishedCustomizationsForMe() {
  const rows = await q_Customizations_PublishedForMe.getAll();
  return Array.isArray(rows) ? rows : [];
}

/**
 * The latest published definition of one accessible customization
 * ({ ID, Name, CustomizationType, Definition }), or null.
 */
export async function getPublishedCustomizationDefinition(customizationID) {
  return await q_Customization_PublishedDefinition.getFirstOrDefault({ id: customizationID });
}

// Updates (UpdateController partial: CustomizationUpdates.cs)
export async function createCustomization({ name, customizationType, definition = null, description = null }) {
  return await update('Customization_Create', { name, customizationType, definition, description }, true);
}

export async function saveCustomizationDraft(customizationID, definition) {
  return await update('Customization_SaveDraft', { customizationID, definition }, true);
}

export async function publishCustomization(customizationID) {
  return await update('Customization_Publish', { customizationID }, true);
}

export async function restoreCustomizationToVersion(customizationID, versionGuid) {
  return await update('Customization_RestoreToVersion', { customizationID, customizationVersionID: versionGuid }, true);
}

export async function renameCustomization(customizationID, name, description) {
  return await update('Customization_Rename', { customizationID, name, description }, true);
}

export async function deleteCustomization(customizationID) {
  return await update('Customization_Delete', { customizationID }, true);
}

/** Replaces the customization's access grants. Empty lists = managers only. */
export async function setCustomizationAccess(customizationID, { users = [], teams = [], accessRoles = [] } = {}) {
  return await update('Customization_SetAccess', { customizationID, users, teams, accessRoles }, true);
}

/**
 * Tests one customization output script IN THE WEB APP (authoring: assistant dialog
 * output tests, manager-only version previews). Same request/response shape as the
 * workflow node-test harness, but gated on customization management — Customization
 * must work without the Workflow feature or the workflow engine service existing.
 */
export async function testCustomizationOutputScript({ name, valueMode, valueTemplate, script, variablesJson, templateValues, localsJson } = {}) {
  const res = await update(
    'Customization_TestOutputScript',
    {
      name: name ?? '',
      valueMode: valueMode ?? 'javascript',
      valueTemplate: valueTemplate ?? '',
      script: script ?? '',
      variablesJson: variablesJson ?? '{}',
      templateValues: templateValues ?? {},
      localsJson: localsJson ?? '{}',
      // This endpoint only serves tests, so casing detection is always on.
      detectCasingMisses: true,
    },
    false
  );
  return foldCasingMissesIntoResult(res);
}

/**
 * Evaluates one output of a PUBLISHED customization for live rendering. Sends only
 * the customization identity, output NAME and runtime context — the server
 * re-validates the caller's access per call and runs the script stored in the
 * published definition, never client-supplied script text.
 */
export async function evaluatePublishedCustomizationOutput({ customizationID, outputName, templateValues, localsJson } = {}) {
  const res = await update(
    'Customization_EvaluatePublishedOutput',
    {
      customizationID: customizationID ?? 0,
      outputName: outputName ?? '',
      templateValues: templateValues ?? {},
      localsJson: localsJson ?? '{}',
    },
    false
  );
  return foldCasingMissesIntoResult(res);
}

/**
 * "Send Chat as Bug": emails the assistant session (description + transcript +
 * server-side diagnostic capture as an expiring log link) to the tenant's
 * configured support address. Returns { sent, error }.
 */
export async function reportAssistantBug({ diagnosticSessionID, description, hostLabel, transcript }) {
  const res = await update('Assistant_ReportBug', { diagnosticSessionID, description, hostLabel, transcript }, true);
  return {
    sent: !!(res?.Sent ?? res?.sent),
    error: (res?.Error ?? res?.error ?? '').toString(),
  };
}
